// InvoiceHistory.js — "Invoices" tab under /billing.
// Lists the team's SaaS subscription invoices (GET /subscription-invoices), with
// status + date filters, pagination, an in-app detail view, and manual "send to email".
import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../../utils/axiosInstance';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, CircularProgress, Alert, Chip, IconButton, TextField, MenuItem, Button,
    Dialog, DialogTitle, DialogContent, DialogActions, Pagination, Stack, Tooltip,
    Snackbar,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SendIcon from '@mui/icons-material/Send';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const PAGE_SIZE = 10;

const STATUS_STYLES = {
    paid: { label: 'Paid', color: 'success' },
    unpaid: { label: 'Unpaid', color: 'warning' },
    void: { label: 'Void', color: 'default' },
};

// Colors for the badge in the InvoiceView header. Mirrors the styling used in
// the support email so the in-app view and the emailed copy look the same.
const STATUS_BADGE_STYLES = {
    paid: { label: 'Paid', bg: '#d1fae5', fg: '#10b981' },
    unpaid: { label: 'Payment Due', bg: '#fef3c7', fg: '#f59e0b' },
    void: { label: 'Void', bg: '#f3f4f6', fg: '#6b7280' },
};

const titleCase = (s) => (s || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const formatCurrency = (amount, currencyCode = 'usd') => {
    const n = typeof amount === 'number' ? amount : parseFloat(amount);
    if (isNaN(n)) return '--';
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency', currency: (currencyCode || 'usd').toUpperCase(),
        }).format(n);
    } catch (e) {
        return `$${n.toFixed(2)}`;
    }
};

const formatDate = (dateString) => {
    if (!dateString) return '--';
    try {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric',
        });
    } catch (e) {
        return dateString;
    }
};

const StatusChip = ({ status }) => {
    const style = STATUS_STYLES[status] || { label: status || 'Unknown', color: 'default' };
    return <Chip label={style.label} color={style.color} size="small" sx={{ fontFamily, fontWeight: 600 }} />;
};

// Full styled invoice rendering for the "View invoice" dialog. Mirrors the
// layout of the support email (dark header, line-items table, totals block)
// so the in-app view feels like a real invoice instead of a label/value list.
const InvoiceView = ({ invoice }) => {
    const status = STATUS_BADGE_STYLES[invoice.status]
        || { label: titleCase(invoice.status) || 'Unknown', bg: '#f3f4f6', fg: '#6b7280' };
    const subtotal = Number(invoice.subtotal) || 0;
    const discount = Number(invoice.discount_amount) || 0;
    const total = Number(invoice.total) || 0;
    const reasonLabel = titleCase(invoice.billing_reason);
    const billedToName = invoice.billed_to_name || invoice.billed_to_email || 'Customer';
    const billedToEmail = invoice.billed_to_email || '';
    const planName = invoice.plan_name || 'Subscription';

    let discountDesc = 'Discount';
    if (discount > 0) {
        if (invoice.discount_type === 'percentage') {
            discountDesc = `Discount (${Number(invoice.discount_value)}% off)`;
        } else if (invoice.discount_type === 'fixed') {
            discountDesc = `Discount (${formatCurrency(invoice.discount_value, invoice.currency)} off)`;
        }
    }

    const metaRow = (label, value) => (
        <Stack direction="row" justifyContent="space-between" sx={{ py: 0.4, gap: 2 }}>
            <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#64748b' }}>
                <Box component="span" sx={{ fontWeight: 700, color: '#475569' }}>{label}: </Box>
                {value}
            </Typography>
        </Stack>
    );

    return (
        <Box sx={{ fontFamily, backgroundColor: '#ffffff' }}>
            {/* Dark header — Klonit brand + status badge */}
            <Box sx={{ backgroundColor: '#1e293b', px: { xs: 3, sm: 4 }, py: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ gap: 2 }}>
                    <Box>
                        <Typography sx={{ color: '#ffffff', fontFamily, fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.1 }}>
                            Klonit
                        </Typography>
                        <Typography sx={{ color: '#94a3b8', fontFamily, fontSize: '0.78rem', mt: 0.25 }}>
                            Subscription Invoice
                        </Typography>
                    </Box>
                    <Box sx={{
                        backgroundColor: status.bg, color: status.fg,
                        px: 2, py: 0.5, borderRadius: '999px',
                        fontFamily, fontSize: '0.75rem', fontWeight: 700,
                        whiteSpace: 'nowrap',
                    }}>
                        {status.label}
                    </Box>
                </Stack>
            </Box>

            {/* Invoice metadata */}
            <Box sx={{ px: { xs: 3, sm: 4 }, pt: 3, pb: 1 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', color: '#1e293b', wordBreak: 'break-all' }}>
                    Invoice {invoice.invoice_number}
                </Typography>
                <Box sx={{ mt: 1.25 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" sx={{ py: 0.3, gap: { xs: 0, sm: 2 } }}>
                        <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#64748b' }}>
                            <Box component="span" sx={{ fontWeight: 700, color: '#475569' }}>Issued: </Box>
                            {formatDate(invoice.issued_at)}
                        </Typography>
                        {reasonLabel && (
                            <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#64748b' }}>
                                <Box component="span" sx={{ fontWeight: 700, color: '#475569' }}>Type: </Box>
                                {reasonLabel}
                            </Typography>
                        )}
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" sx={{ py: 0.3, gap: { xs: 0, sm: 2 } }}>
                        <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#64748b' }}>
                            <Box component="span" sx={{ fontWeight: 700, color: '#475569' }}>Bill To: </Box>
                            {billedToName}
                        </Typography>
                        {billedToEmail && (
                            <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#64748b' }}>
                                {billedToEmail}
                            </Typography>
                        )}
                    </Stack>
                    {invoice.paid_at && metaRow('Paid on', formatDate(invoice.paid_at))}
                    {invoice.due_at && !invoice.paid_at && metaRow('Due', formatDate(invoice.due_at))}
                    {invoice.company_name && metaRow('Company', invoice.company_name)}
                </Box>
            </Box>

            {/* Line items */}
            <Box sx={{ px: { xs: 3, sm: 4 }, pt: 2, pb: 1 }}>
                <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                    <Box sx={{
                        display: 'grid', gridTemplateColumns: { xs: '1.4fr 1.4fr 1fr', sm: '2fr 2fr 1fr' },
                        backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0',
                    }}>
                        <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: '#64748b', px: 2, py: 1.25 }}>
                            Plan
                        </Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: '#64748b', px: 2, py: 1.25 }}>
                            Billing Period
                        </Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: '#64748b', px: 2, py: 1.25, textAlign: 'right' }}>
                            Amount
                        </Typography>
                    </Box>
                    <Box sx={{
                        display: 'grid', gridTemplateColumns: { xs: '1.4fr 1.4fr 1fr', sm: '2fr 2fr 1fr' },
                    }}>
                        <Typography sx={{ fontFamily, fontSize: '0.88rem', color: '#1e293b', fontWeight: 600, px: 2, py: 1.5 }}>
                            {planName}
                        </Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#64748b', px: 2, py: 1.5 }}>
                            {invoice.period_label || '--'}
                        </Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.88rem', color: '#1e293b', px: 2, py: 1.5, textAlign: 'right' }}>
                            {formatCurrency(subtotal, invoice.currency)}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Totals — right aligned summary */}
            <Box sx={{ px: { xs: 3, sm: 4 }, pb: 2 }}>
                <Box sx={{ ml: 'auto', maxWidth: 280 }}>
                    <Stack direction="row" justifyContent="space-between" sx={{ py: 1 }}>
                        <Typography sx={{ fontFamily, fontSize: '0.88rem', color: '#64748b' }}>Subtotal</Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.88rem', color: '#1e293b' }}>
                            {formatCurrency(subtotal, invoice.currency)}
                        </Typography>
                    </Stack>
                    {discount > 0 && (
                        <Stack direction="row" justifyContent="space-between" sx={{ py: 1 }}>
                            <Typography sx={{ fontFamily, fontSize: '0.88rem', color: '#64748b' }}>
                                {discountDesc}
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.88rem', color: '#10b981' }}>
                                -{formatCurrency(discount, invoice.currency)}
                            </Typography>
                        </Stack>
                    )}
                    <Box sx={{ borderTop: '2px solid #e2e8f0', mt: 0.5, pt: 1 }}>
                        <Stack direction="row" justifyContent="space-between">
                            <Typography sx={{ fontFamily, fontSize: '1rem', fontWeight: 700, color: '#1e293b' }}>
                                Total
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '1rem', fontWeight: 800, color: '#10b981' }}>
                                {formatCurrency(total, invoice.currency)}
                            </Typography>
                        </Stack>
                    </Box>
                </Box>
            </Box>

            {/* Delivery tracking footnote (optional) */}
            {invoice.sent_to_email && invoice.sent_at && (
                <Box sx={{ px: { xs: 3, sm: 4 }, pb: 2 }}>
                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', textAlign: 'center', fontStyle: 'italic' }}>
                        Last sent to {invoice.sent_to_email} on {formatDate(invoice.sent_at)}
                    </Typography>
                </Box>
            )}

            {/* Footer */}
            <Box sx={{ backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0', px: { xs: 3, sm: 4 }, py: 2 }}>
                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', textAlign: 'center' }}>
                    This is an automated subscription invoice from Klonit.
                </Typography>
            </Box>
        </Box>
    );
};

const InvoiceHistory = () => {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);

    // filters
    const [statusFilter, setStatusFilter] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // dialogs
    const [viewInvoice, setViewInvoice] = useState(null);
    const [sendInvoice, setSendInvoice] = useState(null);
    const [sendEmail, setSendEmail] = useState('');
    const [sending, setSending] = useState(false);
    const [snackbar, setSnackbar] = useState(null);

    const fetchInvoices = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = { page, limit: PAGE_SIZE };
            if (statusFilter) params.status = statusFilter;
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;
            const res = await axiosInstance.get('/subscription-invoices', { params });
            const data = res.data || {};
            setInvoices(Array.isArray(data.invoices) ? data.invoices : []);
            setTotalPages(data.pages || 1);
            setTotal(data.total || 0);
        } catch (err) {
            console.error('Error fetching subscription invoices:', err);
            setError(err.response?.data?.message || err.message || 'Failed to load invoices.');
        } finally {
            setLoading(false);
        }
    }, [page, statusFilter, startDate, endDate]);

    useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

    // Reset to page 1 whenever a filter changes.
    const onFilterChange = (setter) => (e) => {
        setter(e.target.value);
        setPage(1);
    };

    const clearFilters = () => {
        setStatusFilter('');
        setStartDate('');
        setEndDate('');
        setPage(1);
    };

    const openSendDialog = (invoice) => {
        setSendInvoice(invoice);
        setSendEmail(invoice.billed_to_email || '');
    };

    const handleSend = async () => {
        const email = sendEmail.trim();
        if (!sendInvoice || !email) return;
        setSending(true);
        try {
            await axiosInstance.post(`/subscription-invoices/${sendInvoice.id}/send`, { email });
            setSnackbar({ severity: 'success', message: `Invoice sent to ${email}.` });
            setSendInvoice(null);
            setSendEmail('');
            fetchInvoices();
        } catch (err) {
            setSnackbar({
                severity: 'error',
                message: err.response?.data?.message || 'Failed to send the invoice. Please try again.',
            });
        } finally {
            setSending(false);
        }
    };

    const hasActiveFilters = statusFilter || startDate || endDate;

    return (
        <Box sx={{ fontFamily }}>
            <Typography sx={{ fontFamily, fontSize: '1.1rem', fontWeight: 700, color: '#1e293b', mb: 0.5 }}>
                Invoices
            </Typography>
            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#94a3b8', mb: 2 }}>
                Subscription invoices for your account. Use “Send” to email a copy to any address.
            </Typography>

            {/* Filter bar */}
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                sx={{ mb: 2, alignItems: { xs: 'stretch', sm: 'center' } }}
            >
                <TextField
                    select size="small" label="Status" value={statusFilter}
                    onChange={onFilterChange(setStatusFilter)} sx={{ minWidth: 140, fontFamily }}
                >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="paid">Paid</MenuItem>
                    <MenuItem value="unpaid">Unpaid</MenuItem>
                    <MenuItem value="void">Void</MenuItem>
                </TextField>
                <TextField
                    size="small" label="From" type="date" value={startDate}
                    onChange={onFilterChange(setStartDate)}
                    InputLabelProps={{ shrink: true }} sx={{ minWidth: 160 }}
                />
                <TextField
                    size="small" label="To" type="date" value={endDate}
                    onChange={onFilterChange(setEndDate)}
                    InputLabelProps={{ shrink: true }} sx={{ minWidth: 160 }}
                />
                {hasActiveFilters && (
                    <Button onClick={clearFilters} size="small" sx={{ fontFamily, textTransform: 'none' }}>
                        Clear filters
                    </Button>
                )}
            </Stack>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : error ? (
                <Alert severity="error" sx={{ fontFamily }}>{error}</Alert>
            ) : invoices.length === 0 ? (
                <Typography sx={{ fontFamily, color: '#64748b', py: 4, textAlign: 'center' }}>
                    {hasActiveFilters ? 'No invoices match the selected filters.' : 'No invoices yet.'}
                </Typography>
            ) : (
                <>
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '12px' }}>
                        <Table sx={{ minWidth: 700 }} aria-label="subscription invoices">
                            <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                                <TableRow>
                                    <TableCell sx={{ fontFamily, fontWeight: 700 }}>Invoice #</TableCell>
                                    <TableCell sx={{ fontFamily, fontWeight: 700 }}>Issued</TableCell>
                                    <TableCell sx={{ fontFamily, fontWeight: 700 }}>Billing Period</TableCell>
                                    <TableCell sx={{ fontFamily, fontWeight: 700 }} align="right">Amount</TableCell>
                                    <TableCell sx={{ fontFamily, fontWeight: 700 }}>Status</TableCell>
                                    <TableCell sx={{ fontFamily, fontWeight: 700 }} align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {invoices.map((inv) => (
                                    <TableRow key={inv.id} hover>
                                        <TableCell sx={{ fontFamily, fontWeight: 600 }}>{inv.invoice_number}</TableCell>
                                        <TableCell sx={{ fontFamily }}>{formatDate(inv.issued_at)}</TableCell>
                                        <TableCell sx={{ fontFamily, color: '#64748b' }}>{inv.period_label || '--'}</TableCell>
                                        <TableCell sx={{ fontFamily }} align="right">
                                            {formatCurrency(inv.total, inv.currency)}
                                        </TableCell>
                                        <TableCell><StatusChip status={inv.status} /></TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="View invoice">
                                                <IconButton size="small" onClick={() => setViewInvoice(inv)}>
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Send to email">
                                                <IconButton size="small" onClick={() => openSendDialog(inv)}>
                                                    <SendIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 1 }}>
                        <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#94a3b8' }}>
                            {total} invoice{total === 1 ? '' : 's'}
                        </Typography>
                        {totalPages > 1 && (
                            <Pagination
                                count={totalPages} page={page}
                                onChange={(e, value) => setPage(value)}
                                size="small" color="primary"
                            />
                        )}
                    </Box>
                </>
            )}

            {/* View dialog — full invoice layout, mirrors the support email. */}
            <Dialog
                open={!!viewInvoice} onClose={() => setViewInvoice(null)}
                maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden' } }}
            >
                {viewInvoice && (
                    <>
                        <DialogContent sx={{ p: 0 }}>
                            <InvoiceView invoice={viewInvoice} />
                        </DialogContent>
                        <DialogActions sx={{ px: 3, py: 2, backgroundColor: '#ffffff', borderTop: '1px solid #e2e8f0' }}>
                            <Button onClick={() => setViewInvoice(null)} sx={{ fontFamily, textTransform: 'none' }}>
                                Close
                            </Button>
                            <Button
                                variant="contained" startIcon={<SendIcon />}
                                onClick={() => { openSendDialog(viewInvoice); setViewInvoice(null); }}
                                sx={{ fontFamily, textTransform: 'none' }}
                            >
                                Send to email
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Send dialog */}
            <Dialog open={!!sendInvoice} onClose={() => !sending && setSendInvoice(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Send Invoice</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b', mb: 2 }}>
                        Email a copy of invoice {sendInvoice?.invoice_number} to the address below.
                    </Typography>
                    <TextField
                        autoFocus fullWidth size="small" label="Email address" type="email"
                        value={sendEmail} onChange={(e) => setSendEmail(e.target.value)}
                        disabled={sending}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSendInvoice(null)} disabled={sending} sx={{ fontFamily, textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained" onClick={handleSend}
                        disabled={sending || !sendEmail.trim()}
                        startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                        sx={{ fontFamily, textTransform: 'none' }}
                    >
                        {sending ? 'Sending...' : 'Send'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!snackbar} autoHideDuration={6000} onClose={() => setSnackbar(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                {snackbar ? (
                    <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)} sx={{ fontFamily }}>
                        {snackbar.message}
                    </Alert>
                ) : null}
            </Snackbar>
        </Box>
    );
};

export default InvoiceHistory;
